import {
  PublicClientApplication,
  EventType,
  type AccountInfo,
  type SilentRequest,
  type EventMessage,
  type AuthenticationResult,
} from "@azure/msal-browser";
import {
  TENANT_ID,
  SPA_CLIENT_ID,
  REDIRECT_URI,
  POST_LOGOUT_URI,
  VITE_ENTRA_APP_ID_URI,
  ENABLE_MOCK_AUTH,
  ALLOW_MOCK_AUTH_IN_PROD,
} from "@/lib/env";
import { logger } from "@/utils/logger";

const tenantId = TENANT_ID as string | undefined;
const clientId = SPA_CLIENT_ID as string | undefined;
const redirectUri =
  (REDIRECT_URI as string | undefined) || `${window.location.origin}/react/`;
const postLogoutUri =
  (POST_LOGOUT_URI as string | undefined) || `${window.location.origin}/react/`;
const entraAppIdUri = VITE_ENTRA_APP_ID_URI as string | undefined;
const useMockAuth = ENABLE_MOCK_AUTH;
const allowMockAuthInProd = ALLOW_MOCK_AUTH_IN_PROD;

export const apiScopes = {
  scopes: [`${entraAppIdUri}/App.Access`],
};

export const graphScopes = {
  scopes: [
    "openid",
    "profile",
    "https://graph.microsoft.com/User.Read",
    "https://graph.microsoft.com/User.Read.All",
  ],
};

if (useMockAuth && import.meta.env.PROD && !allowMockAuthInProd) {
  throw new Error(
    "Mock authentication is disabled for production builds. Remove VITE_ENABLE_MOCK_AUTH or enable VITE_ALLOW_MOCK_AUTH_IN_PROD explicitly for controlled CI usage.",
  );
}

if (!tenantId || !clientId) {
  if (useMockAuth) {
    logger.warn(
      "[MSAL] Missing Azure credentials, running with mock authentication.",
    );
  } else {
    throw new Error(
      "Missing MSAL configuration: ensure VITE_AAD_TENANT_ID and VITE_AAD_SPA_CLIENT_ID are defined.",
    );
  }
}

const resolvedClientId = clientId ?? "00000000-0000-0000-0000-000000000000";

export const msalConfig = {
  auth: {
    clientId: resolvedClientId,
    authority: `https://login.microsoftonline.com/organizations`,
    redirectUri,
    postLogoutUri,
  },
  cache: {
    cacheLocation: "localStorage", // Using localStorage for Playwright test compatibility
    storeAuthStateInCookie: false,
  },
};

// Note: In React, authentication state will be managed via Context API or Jotai
// This is kept for compatibility with copied classes, will be refactored to use React state management
export const state = {
  isAuthenticated: false,
  user: null as AccountInfo | null,
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Track initialization status
let msalInitialized = false;
let msalInitPromise: Promise<void> | null = null;

/**
 * Initialize MSAL instance. This must be called before using any MSAL APIs.
 * Safe to call multiple times - will only initialize once.
 */
export const initializeMsal = async (): Promise<void> => {
  if (msalInitialized) return;
  if (msalInitPromise) return msalInitPromise;

  if (useMockAuth) {
    msalInitialized = true;
    state.isAuthenticated = true;
    msalInitPromise = Promise.resolve();
    return msalInitPromise;
  }

  msalInitPromise = msalInstance.initialize().then(() => {
    msalInitialized = true;

    // Set active account on initial load if accounts exist in cache
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      state.user = accounts[0];
      state.isAuthenticated = true;
      logger.debug(
        "MSAL initialized with existing account:",
        accounts[0]?.username,
      );
    }
  });

  return msalInitPromise;
};

/**
 * Event callback to handle LOGIN_SUCCESS and set active account.
 * This is the recommended pattern from Microsoft when using MsalProvider.
 * MsalProvider automatically calls handleRedirectPromise, so we listen for events instead.
 */
if (!useMockAuth) {
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
      state.user = account;
      state.isAuthenticated = true;
      logger.debug("MSAL LOGIN_SUCCESS, account:", account?.username);
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      state.user = null;
      state.isAuthenticated = false;
      logger.debug("MSAL LOGOUT_SUCCESS");
    }

    if (event.eventType === EventType.HANDLE_REDIRECT_END) {
      // Check if we have accounts after redirect handling completes
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
        // Set active account if not already set
        msalInstance.setActiveAccount(accounts[0]);
        state.user = accounts[0];
        state.isAuthenticated = true;
        logger.debug(
          "MSAL HANDLE_REDIRECT_END, setting active account:",
          accounts[0]?.username,
        );
      }
    }
  });
}

export const acquireApiToken = async (
  account?: AccountInfo | null,
): Promise<string | null> => {
  if (useMockAuth) {
    return "mock-token";
  }

  const activeAccount =
    account ||
    msalInstance.getActiveAccount() ||
    state.user ||
    msalInstance.getAllAccounts()[0] ||
    null;
  if (!activeAccount) return null;

  const request: SilentRequest = {
    scopes: apiScopes.scopes,
    account: activeAccount,
  };

  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return result.accessToken;
  } catch {
    // If interaction is required, fall back to redirect to keep UX consistent
    try {
      await msalInstance.acquireTokenRedirect({ scopes: apiScopes.scopes });
    } catch {
      // swallow; caller can decide what to do next
    }
    return null;
  }
};
